import React, { useState } from 'react';
import { apiService } from '../services/apiService';
import type { Shop, ShopOutcomeRow } from '../services/apiService';
import { shopHealth, metaStyle as meta, warningStyle as warn } from '../utils/shopHealth';
import { stateLine, figures, toneColour } from '../utils/shopPresentation';
import FeedRunHistory from './FeedRunHistory';

// ONE SHOP, WHOLE. Every figure it has, every sentence about it, every action it
// owns — the things a 43-row list could not carry without becoming 19 screens of
// prose.
//
// It is the second half of the `/feeds` split: the hub says WHICH shop and
// WHETHER it is fine, and this says everything else. Nothing here is new. The
// blocks below were rendered on the row before, four to a shop, forty-three
// times over.
//
// THE ACTIONS MOVED WITH THEIR GATES UNCHANGED, and that is checked from outside
// this file: `test-fixtures/shopStates.ts` records which controls each shop state
// offers, and both this component's suite and the hub's assert against it. The
// failure mode of a move is losing a control, and 101 buttons across 43 rows is
// too many to check by reading.

export interface ShopRoomProps {
  shop: Shop;
  outcome: ShopOutcomeRow | null;
  outcomeWindowDays: number | null;
  /** Whether the hub listed this shop in its attention group — see the disclosure. */
  needsAttention: boolean;
  /** Reload the shop after an action that changed it. */
  onChanged: () => void | Promise<void>;
  /**
   * The shop no longer exists. Delete and Remove destroy this room's own
   * subject, so the caller navigates away and carries the notice with it —
   * otherwise a publisher who has just succeeded is told the shop is no longer
   * in the offer.
   */
  onGone: (notice: string) => void;
}

type BusyAction = 'add' | 'sync' | 'toggle' | 'remove' | 'delete' | 'advertiser' | 'url';

const editorPanel: React.CSSProperties = {
  marginTop: '0.6rem', paddingTop: '0.6rem', borderTop: '1px solid var(--line)',
};
const editorInput: React.CSSProperties = {
  maxWidth: '420px', padding: '0.4rem', border: '1px solid var(--line)',
  borderRadius: 'var(--radius)', boxSizing: 'border-box',
};
const editorActions: React.CSSProperties = { marginTop: '0.4rem', display: 'flex', gap: '0.4rem' };

const ShopRoom: React.FC<ShopRoomProps> = ({
  shop, outcome, outcomeWindowDays, needsAttention, onChanged, onGone,
}) => {
  // WHICH ACTION, not just that one is running. Holding the row alone made two
  // buttons choose their progress label off the same value, so pressing "Pause
  // sync" rendered `Syncing… | Pause sync | Removing… | Delete`. One shop's worth
  // of buttons does not make that mistake impossible, so the shape is kept.
  const [busy, setBusy] = useState<BusyAction | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [editingUrl, setEditingUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');
  const [editingAdvertiser, setEditingAdvertiser] = useState(false);
  const [advertiserDraft, setAdvertiserDraft] = useState('');
  const [suggestNotice, setSuggestNotice] = useState('');

  const state = stateLine(shop);
  const health = shopHealth(shop);
  // Tier 3 of advertiser resolution: the manual fallback for a feed whose links
  // and own advertiser column both leave its products unidentified.
  const unidentified = shop.feedId !== null && shop.programmeKey === null;

  /** Every action shares one busy value, one error line and one reload. */
  const run = async (action: BusyAction, work: () => Promise<string>) => {
    setBusy(action); setProblem(null); setNotice(null);
    try {
      setNotice(await work());
      await onChanged();
    } catch (e: any) {
      setProblem(e?.message || 'That did not work.');
    } finally {
      setBusy(null);
    }
  };

  /**
   * An action that destroys this shop. It does NOT reload — there would be
   * nothing to find — it hands the notice to the caller, which navigates.
   */
  const runFinal = async (action: BusyAction, work: () => Promise<string>) => {
    setBusy(action); setProblem(null); setNotice(null);
    try {
      onGone(await work());
    } catch (e: any) {
      setProblem(e?.message || 'That did not work.');
      setBusy(null);
    }
  };

  const k = shop.key;

  return (
    <section className="stats-section" data-testid={`shop-room-${k}`}>
      {problem && <p role="alert" data-testid="shop-room-problem" style={{ color: 'var(--danger)' }}>{problem}</p>}
      {notice && <p role="status" data-testid="shop-room-notice">{notice}</p>}

      <h2 style={{ marginBottom: '0.25rem' }}>{shop.label}</h2>
      <div>
        {shop.category && <span style={meta}>{shop.category}</span>}
        {health && <span style={warn} data-testid={`health-${k}`}>{health}</span>}
      </div>

      <div data-testid={`state-${k}`} style={{ ...meta, color: toneColour[state.tone], marginTop: '0.5rem' }}>
        {state.text}
      </div>

      <div style={{ ...meta, marginTop: '0.35rem' }}>{figures(shop).join(' · ')}</div>

      {/* THE ONLY FIGURE HERE ABOUT THIS PUBLISHER. Everything above describes
          the shop across the whole network. Counts, never a rate: a percentage
          invites comparing 20 clicks in 96 shows with 1 in 121, and the second
          number cannot support one. */}
      {outcome && (
        <div style={{ ...meta, marginTop: '0.35rem' }} data-testid={`outcome-${k}`}>
          {outcome.shows.toLocaleString()} shown · {outcome.clicks.toLocaleString()} clicked
          {outcome.shownNeverClicked && (
            <span data-testid={`unclicked-${k}`} style={{ color: 'var(--warn, #8a5a00)', fontWeight: 600 }}>
              {' '}— never clicked
            </span>
          )}
          {outcomeWindowDays !== null && <span style={{ opacity: 0.7 }}> · last {outcomeWindowDays} days</span>}
        </div>
      )}

      {/* A feed covering several advertisers is one shop. Naming them is what
          stops it reading as a shop it is not. */}
      {shop.coversHosts.length > 0 && (
        <div style={{ ...meta, marginTop: '0.35rem' }} data-testid={`covers-${k}`}>
          Covers {shop.coversHosts.join(', ')}
        </div>
      )}

      {/* Served verbatim, so the publisher can see WHY a shop sits where it does
          — which is what stops the ordering being a number nobody can argue
          with. It is disclosed rather than deleted (recorded Decision), and it
          lives here rather than on the row because measured against the real
          account it ran 247 characters per row on 43 of 43 rows, against 56 for
          every number on the row combined.

          WHEN THE HUB LISTED THIS SHOP FIRST, THE SERVER'S REASONS ARE NOT THE
          WHOLE REASON. The hub groups shops needing attention ahead of the
          merchant prior's order, so the served sentences would otherwise be
          explaining a position the shop does not hold. */}
      {(needsAttention || shop.priorReasons.length > 0) && (
        <details style={{ ...meta, marginTop: '0.5rem' }} data-testid={`why-${k}`}>
          <summary style={{ cursor: 'pointer' }}>Why it ranks here</summary>
          {needsAttention && (
            <p style={{ margin: '0.35rem 0' }} data-testid={`why-attention-${k}`}>
              Listed first because it needs attention. What your network reports about the shop
              itself is below, and did not decide that.
            </p>
          )}
          {shop.priorReasons.join(' ')}
        </details>
      )}

      {shop.suppliedByFeedLabel && (
        <div style={{ ...meta, marginTop: '0.35rem' }} data-testid={`supplied-by-${k}`}>
          Already supplied by your “{shop.suppliedByFeedLabel}” feed.
        </div>
      )}

      {/* Two supplies for one shop means its products are in every match twice.
          Reported rather than resolved for them: only the publisher can say
          which one they meant to keep. */}
      {shop.duplicateSupply && (
        <div data-testid={`duplicate-${k}`} style={{ ...meta, marginTop: '0.35rem', color: 'var(--warn, #8a5a00)', fontWeight: 600 }}>
          This shop is supplied twice — by your own feed and by a shop you added — so its
          products are counted twice. Remove one.
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '1rem' }}>
        {/* GATED ON `supplied`, NEVER ON `supply`. They are not the same
            question: `supply` describes THIS shop's own feed or subscription,
            while `supplied` also covers a shop whose products already arrive
            through ANOTHER row's feed. Such a shop is `supply: 'none'` and must
            still offer no Add, or the screen says "already supplied by your X
            feed" with an Add button beneath it. */}
        {!shop.supplied && shop.programmeKey !== null && (
          <button
            type="button" data-testid={`add-${k}`} className="btn btn--primary" disabled={busy !== null}
            onClick={() => run('add', async () => {
              const result = await apiService.subscribeToCatalogue(shop.network, shop.programmeKey as string);
              return result.state === 'preparing'
                ? `Fetching ${shop.label}’s products — this one is new to us, so it takes a few minutes. You can leave this page.`
                : `${shop.label} added. Its products are already available.`;
            })}
          >
            {busy === 'add' ? 'Adding…' : 'Add'}
          </button>
        )}

        {/* A URL feed's sync actions. A file-upload feed has no sync to act on,
            so it renders none rather than a button that does nothing.

            SYNC NOW IS OFFERED ONLY WHILE THE SCHEDULE IS ON, and that gate is a
            correctness rule rather than tidiness. `runManualSync` deliberately
            never touches `sync_enabled` (`feed-sync/scheduler.ts`: "the automatic
            schedule is untouched by a publisher testing a URL by hand"), while a
            SUCCESSFUL sync clears `last_sync_status`/`last_sync_error`. Those two
            columns are the only thing that tells a strike from a pause
            (`shop-list.ts`'s `feedSupplyState`), so a manual sync that succeeded
            on a struck-out feed left `(url, disabled, ok)` — which reads as
            `paused`. The room then said "You paused syncing" about a feed the
            publisher had just proved healthy, the hub dropped it out of "Needs
            you", and `WHERE sync_enabled` never selected it again: the feed stops
            refreshing for good, quietly, at the moment things went right.

            THE SAME DEFECT WAS FOUND AND FIXED ONCE ALREADY, on the other repair
            path — `updateFeedSync`'s `sourceUrl` branch carries that story in its
            own comment and resumes the schedule for exactly this reason. This was
            the second door into it.

            `Resume sync` is the whole remedy and needs no other button: it clears
            the strike counters and sets `next_sync_at = now()`, so the scheduler
            picks the feed up on its next five-minute tick and the row then says
            what actually happened. `!== false`, not `!`: `null` is "no feed of
            its own", never "off". */}
        {/* Withheld while WE are rebuilding the source: Sync now would fetch
            the dead export again, and Resume would reset the failure count the
            repair selects on — a button that undoes the fix it sits beside. */}
        {shop.feedId !== null && shop.sourceUrlDisplay && shop.supply !== 'repairing' && (
          <>
            {shop.syncEnabled !== false && (
            <button
              type="button" data-testid={`sync-${k}`} className="btn btn--primary" disabled={busy !== null}
              onClick={() => run('sync', async () => {
                // The server answers two different things — a processed count,
                // or "unchanged" — and both used to be replaced with a constant
                // "synced.", so a feed whose source had not moved was reported
                // as freshly synced. `processedProducts` is present on one
                // branch and absent on the other, which tells them apart.
                const res = await apiService.syncFeedNow(shop.feedId as number);
                const processed = res.result?.processedProducts;
                return typeof processed === 'number'
                  ? `${shop.label} synced — ${processed.toLocaleString()} products.`
                  : `${shop.label} was already up to date — its source has not changed.`;
              })}
            >
              {busy === 'sync' ? 'Syncing…' : 'Sync now'}
            </button>
            )}
            {/* Primary when it is the RESUME: with Sync now withheld, this is the
                only action on the screen that puts a stopped or paused feed back
                to work, and the two other buttons beside it are destructive. */}
            <button
              type="button" data-testid={`toggle-sync-${k}`}
              className={`btn btn--${shop.syncEnabled === false ? 'primary' : 'secondary'}`}
              disabled={busy !== null}
              onClick={() => run('toggle', async () => {
                const enable = !shop.syncEnabled;
                await apiService.updateFeedSync(shop.feedId as number, { syncEnabled: enable });
                return enable ? `${shop.label} will sync again.` : `${shop.label} paused.`;
              })}
            >
              {busy === 'toggle'
                ? (shop.syncEnabled ? 'Pausing…' : 'Resuming…')
                : (shop.syncEnabled ? 'Pause sync' : 'Resume sync')}
            </button>
          </>
        )}

        {/* Not offered for a URL WE authored. `source_url` is write-only — the
            publisher is shown only a redacted form — so editing a machine URL
            they never typed and cannot read back is a one-way door. The server
            refuses it too (409). */}
        {shop.feedId !== null && !shop.sourceManaged && (
          <button
            type="button" data-testid={`edit-url-${k}`} className="btn btn--secondary"
            onClick={() => { setEditingUrl(v => !v); setUrlDraft(''); }}
          >
            {shop.sourceUrlDisplay ? 'Edit URL' : 'Add URL'}
          </button>
        )}

        {/* Removing a subscription is offered in every state including
            `preparing`: a subscription stuck mid-build must still be undoable. */}
        {shop.catalogueId !== null && (
          <button
            type="button" data-testid={`remove-${k}`} className="btn btn--danger" disabled={busy !== null}
            onClick={() => runFinal('remove', async () => {
              await apiService.unsubscribeFromCatalogue(shop.catalogueId as number);
              return `${shop.label} removed. We will stop refreshing it; the export stays in your network account until you delete it there.`;
            })}
          >
            {busy === 'remove' ? 'Removing…' : 'Remove'}
          </button>
        )}

        {unidentified && (
          <button
            type="button" data-testid={`edit-advertiser-${k}`} className="btn btn--secondary"
            onClick={() => {
              setEditingAdvertiser(v => !v);
              setAdvertiserDraft(shop.declaredAdvertiserHost || '');
              setSuggestNotice('');
            }}
          >
            {shop.declaredAdvertiserHost ? `Advertiser: ${shop.declaredAdvertiserHost}` : 'Set advertiser'}
          </button>
        )}

        {shop.feedId !== null && (
          <button
            type="button" data-testid={`delete-feed-${k}`} className="btn btn--danger" disabled={busy !== null}
            onClick={() => {
              const ok = window.confirm(
                `Delete the ${shop.label} feed and all ${shop.productCount ?? 0} of its products? `
                + 'Your widgets will stop matching them. This cannot be undone.'
              );
              if (!ok) return;
              void runFinal('delete', async () => {
                await apiService.deleteFeed(shop.feedId as number);
                return `${shop.label} deleted.`;
              });
            }}
          >
            {busy === 'delete' ? 'Deleting…' : 'Delete'}
          </button>
        )}
      </div>

      {/* Only a shop with a feed HAS runs. A catalogue subscription's products
          arrive through a build we do not log here, and an addable shop has
          nothing at all — a panel offering to show either would be a promise of
          evidence that does not exist. */}
      {shop.feedId !== null && <FeedRunHistory feedId={shop.feedId} label={shop.label} />}

      {/* THE EXCEPTION IS THE POINT — the same sentence the hub carries, because
          this is where most of those network figures now live. It once said
          "these figures" with no exception, which stopped being true the day
          "shown" and "clicked" arrived. Adding a figure here means revisiting
          it. */}
      <p style={{ ...meta, marginTop: '1rem' }}>
        Apart from “shown” and “clicked”, which are yours, these figures describe how this
        shop performs for affiliates across the whole network — not for your readers
        specifically.
      </p>

      {editingAdvertiser && (
        <div style={editorPanel}>
          <p style={{ ...meta, margin: '0 0 0.4rem' }}>
            Used only for products this feed cannot otherwise identify an advertiser for — most
            feeds never need this. Leave blank to clear it.
          </p>
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              data-testid={`advertiser-input-${k}`} type="text"
              aria-label={`Advertiser for ${shop.label}`} placeholder="e.g. dyfashion.ro"
              value={advertiserDraft} onChange={e => setAdvertiserDraft(e.target.value)}
              style={{ ...editorInput, flex: '1 1 220px' }}
            />
            <button
              type="button" data-testid={`suggest-advertiser-${k}`} className="btn btn--primary" disabled={busy !== null}
              onClick={async () => {
                setSuggestNotice('');
                try {
                  const result = await apiService.suggestDeclaredAdvertiser(shop.feedId as number);
                  if (result.suggestion) {
                    setAdvertiserDraft(result.suggestion);
                    setSuggestNotice(`Your feed’s own products agree: ${result.suggestion}`);
                  } else if (result.distinctValues.length > 1) {
                    setSuggestNotice(`Your feed names more than one advertiser: ${result.distinctValues.join(', ')}. Pick one, or leave this feed as-is.`);
                  } else {
                    setSuggestNotice('No advertiser could be read from this feed’s own products yet.');
                  }
                } catch (e: any) {
                  setSuggestNotice(e?.message || 'Could not suggest an advertiser');
                }
              }}
            >
              Suggest
            </button>
          </div>
          {suggestNotice && (
            <div data-testid={`suggest-advertiser-notice-${k}`} style={{ ...meta, marginTop: '0.35rem' }}>
              {suggestNotice}
            </div>
          )}
          <div style={editorActions}>
            <button
              type="button" data-testid={`save-advertiser-${k}`} className="btn btn--primary" disabled={busy !== null}
              onClick={() => run('advertiser', async () => {
                const trimmed = advertiserDraft.trim();
                await apiService.updateFeedDeclaredAdvertiser(shop.feedId as number, trimmed || null);
                setEditingAdvertiser(false);
                return trimmed
                  ? `Advertiser for “${shop.label}” set to ${trimmed}.`
                  : `Cleared the advertiser for “${shop.label}”.`;
              })}
            >
              Save
            </button>
            <button type="button" className="btn btn--secondary" onClick={() => setEditingAdvertiser(false)}>Cancel</button>
          </div>
        </div>
      )}

      {editingUrl && (
        <div style={editorPanel}>
          {shop.sourceUrlDisplay && (
            <div style={{ ...meta, marginBottom: '0.35rem' }}>
              Current: <code>{shop.sourceUrlDisplay}</code>
            </div>
          )}
          <input
            data-testid={`url-input-${k}`} type="text" inputMode="url"
            autoCapitalize="off" autoCorrect="off" spellCheck={false}
            aria-label={`Feed URL for ${shop.label}`} placeholder="https://example.com/feed.csv"
            value={urlDraft} onChange={e => setUrlDraft(e.target.value)}
            style={{ ...editorInput, width: '100%' }}
          />
          <div style={editorActions}>
            <button
              type="button" data-testid={`save-url-${k}`} className="btn btn--primary"
              disabled={busy !== null || !urlDraft.trim()}
              onClick={() => run('url', async () => {
                await apiService.updateFeedSync(shop.feedId as number, { sourceUrl: urlDraft.trim() });
                setEditingUrl(false);
                return `${shop.label}’s feed URL saved.`;
              })}
            >
              Save URL
            </button>
            <button type="button" className="btn btn--secondary" onClick={() => setEditingUrl(false)}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
};

export default ShopRoom;
